import { Box, Button, CircularProgress, IconButton, Slider, ThemeProvider, Typography, useTheme } from '@mui/material'
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { observer } from 'mobx-react'

import { VideoPlayerStore, defaultControls, VideoPlayerStoreProvider, useVideoPlayerStore, VIDEO_PLAYER_STATUS } from '../../../store/video-player.store'
import { EMPTY_ARRAY, EMPTY_OBJECT, formatVideoDuration, Icons } from '../../../utils/utilities'
import noVideosImg from '../../../assets/norecording.svg'
import VideoPlayerTimeline from './VideoPlayerTimeline'
import themes from '../../../themes/themes'
import VolumeControl from './VolumeControl'
import VideoProgress from './VideoProgress'

const VideoPlayerInner = observer( ( {
    editOptions = {},
    src,
    containerHeight,
    title,
    thumb,
    defaults = {},
    events = [],
    editMode = false,
    onEventsChange,
    onEvent,
    autoPlay = false,
    containerStyles = {},
    onReady,
    ...rest
} ) => {

    const store = useVideoPlayerStore()

    const finalControls = store.controllerOptions

    useEffect( () => {
        if ( !store.draggingEventId ) return

        const handlePointerMove = ( event ) => store.handleEventDrag( event )
        const handlePointerUp = () => store.handleEventDragStop()

        document.addEventListener( 'pointermove', handlePointerMove )
        document.addEventListener( 'touchmove', handlePointerMove, { passive: false } )
        document.addEventListener( 'pointerup', handlePointerUp )
        document.addEventListener( 'touchend', handlePointerUp )
        document.addEventListener( 'touchcancel', handlePointerUp )

        return () => {
            document.removeEventListener( 'pointermove', handlePointerMove )
            document.removeEventListener( 'touchmove', handlePointerMove, { passive: false } )
            document.removeEventListener( 'pointerup', handlePointerUp )
            document.removeEventListener( 'touchend', handlePointerUp )
            document.removeEventListener( 'touchcancel', handlePointerUp )
        }
    }, [store, store.draggingEventId] )



    useEffect( () => {
        const videoContainer = store.videoContainerRef

        const handleTimelineMove = ( event ) => {
            if ( event.type === "touchmove" )
                event.preventDefault()

            store.handleMouseOnTimeline( event, store.isSeeking, store.controllerOptions.seeking && store.controllerOptions.seekingforward )
        }

        const mouseUpHandler = async ( event ) => {
            await store.endSeeking( event )
        }

        videoContainer?.addEventListener( 'fullscreenchange', store.handleFullScreenChange )
        document.addEventListener( 'pointerup', mouseUpHandler )
        document.addEventListener( 'touchend', mouseUpHandler )
        document.addEventListener( 'touchcancel', mouseUpHandler )
        document.addEventListener( 'pointermove', handleTimelineMove )
        document.addEventListener( 'touchmove', handleTimelineMove, { passive: false } )
        window.screen.orientation?.addEventListener( 'change', store.handleFullScreenChange )

        return () => {
            videoContainer?.removeEventListener( 'fullscreenchange', store.handleFullScreenChange )
            document.removeEventListener( 'pointerup', mouseUpHandler )
            document.removeEventListener( 'touchend', mouseUpHandler )
            document.removeEventListener( 'touchcancel', mouseUpHandler )
            document.removeEventListener( 'pointermove', handleTimelineMove )
            document.removeEventListener( 'touchmove', handleTimelineMove, { passive: false } )
            window.screen.orientation?.removeEventListener( 'change', store.handleFullScreenChange )
            if ( store.overlayDisappearTimeoutId ) {
                clearTimeout( store.overlayDisappearTimeoutId )
                store.overlayDisappearTimeoutId = null
            }
        }
    }, [store] )

    useEffect( () => {
        store.setMaxTimeWatched( 0 )
    }, [src, store] )


    useEffect( () => {
        const video = store.videoPlayerRef
        if ( !video ) return

        const handleError = ( event ) => {
            store.setError( true )
        }

        const handleCanPlay = ( event ) => {
            store.handleOnCanPlay( event )
        }

        const handleWaiting = ( event ) => {
            store.setIsBuffering( true )
        }

        const handlePlay = ( event ) => {
            store.handlePlay()
        }

        const handlePause = ( event ) => {
            store.handlePause()
        }

        const handleVolumeChange = ( event ) => {
            store.handleVolumeChange()
        }

        const handleLoadedMetadata = ( event ) => {
            store.handleLoadedMetadata()
            if ( onReady && typeof onReady === 'function' )
                onReady( store )
        }

        const handleProgress = ( event ) => {
            store.handleVideoBuffer()
        }

        const handleEnded = ( event ) => {
            store.setShowReplayBtn( true )
        }

        const handleTimeUpdate = ( event ) => {
            store.updateProgress()
        }

        const handleDurationUpdate = ( event ) => {
            store.calculateDuration()
        }

        video.addEventListener( 'error', handleError )
        video.addEventListener( 'canplay', handleCanPlay )
        video.addEventListener( 'waiting', handleWaiting )
        video.addEventListener( 'play', handlePlay )
        video.addEventListener( 'pause', handlePause )
        video.addEventListener( 'volumechange', handleVolumeChange )
        video.addEventListener( 'loadedmetadata', handleLoadedMetadata )
        video.addEventListener( 'progress', handleProgress )
        video.addEventListener( 'ended', handleEnded )
        video.addEventListener( 'timeupdate', handleTimeUpdate )
        video.addEventListener( 'durationchange', handleDurationUpdate )

        return () => {
            video.removeEventListener( 'error', handleError )
            video.removeEventListener( 'canplay', handleCanPlay )
            video.removeEventListener( 'waiting', handleWaiting )
            video.removeEventListener( 'play', handlePlay )
            video.removeEventListener( 'pause', handlePause )
            video.removeEventListener( 'volumechange', handleVolumeChange )
            video.removeEventListener( 'loadedmetadata', handleLoadedMetadata )
            video.removeEventListener( 'progress', handleProgress )
            video.removeEventListener( 'ended', handleEnded )
            video.removeEventListener( 'timeupdate', handleTimeUpdate )
            video.removeEventListener( 'durationchange', handleDurationUpdate )
        }
    }, [store] )

    return (
        <ThemeProvider theme={themes.dark}>
            <Box
                height={containerHeight}
                ref={store.setVideoContainerRef}
                onPointerEnter={( e ) => store.handleMouseEnter( e )}
                onPointerMove={( e ) => store.handleMouseEnter( e )}
                onPointerLeave={() => store.handleMouseLeave( store.controlsContainerRef )}
                tabIndex={0}
                onKeyDown={( e ) => store.handleKeyDown( e )}
                className="video-container"
                id="video-container"
                width={{ lg: '100%', xs: '100%' }}
                sx={containerStyles}
            >
                {store.error && !store.attachmentDataLoading && (
                    <Box display="flex" flexDirection="column" gap="20px" padding="50px" alignItems="center" justifyContent="center">
                        <img width="150px" src={noVideosImg} alt="Video fetch error" />
                        <Typography variant="subtitle2" textAlign="center">
                            Oops! something went wrong! couldn't fetch the video frames
                        </Typography>
                    </Box>
                )}
                {!store.error && !store.attachmentDataLoading && (
                    <video
                        thumb={thumb}
                        ref={store.setVideoPlayerRef}
                        id="video"
                        className="video-player"
                        src={src}
                        autoPlay={autoPlay}
                        {...rest}
                    />
                )}

                {!store.error && !store.attachmentDataLoading && store.controls && (
                    <Box width="100%" ref={store.setControlsContainerRef} id="controls-container" height="100%" className={`video-controls-container paused${store.isEditModeActive ? ' edit-mode-active' : ''}`}>
                        <Box className="pause-play-box">
                            {![VIDEO_PLAYER_STATUS.LOADING, VIDEO_PLAYER_STATUS.BUFFERING].includes( store.videoPlayerStatus ) && !store.isEditModeActive && (
                                <Box sx={{ cursor: 'pointer', inset: '0' }} onClick={() => store.togglePlay()} position="absolute" display="flex" alignItems="center" justifyContent="center">
                                    <Box className="play-pause-btn-container" sx={{ opacity: store.isPaused ? 1 : 0 }}>
                                        <IconButton className='play-pause-btn'>
                                            {!store.showReplayBtn ? ( store.isPaused ? Icons.default.PlayArrowIcon : Icons.default.PauseIcon ) : (
                                                Icons.default.Replay
                                            )}
                                        </IconButton>
                                    </Box>
                                </Box>
                            )}
                            {[VIDEO_PLAYER_STATUS.LOADING, VIDEO_PLAYER_STATUS.BUFFERING].includes( store.videoPlayerStatus ) && (
                                <Box onClick={store.togglePlay} position="absolute" sx={{ inset: 0 }} display="flex" alignItems="center" justifyContent="center">
                                    <CircularProgress sx={{ color: 'white' }} size={40} />
                                </Box>
                            )}
                        </Box>

                        {store.videoPlayerStatus !== VIDEO_PLAYER_STATUS.LOADING && <Box className="title-and-timeline">
                            <div className="overlay" />
                            {title && (
                                <Box className="video-title">
                                    <Typography fontWeight="500" noWrap height="fit-content" fontSize="0.9rem" variant="body2" color="white">
                                        {title}
                                    </Typography>
                                </Box>
                            )}
                            <Box id="controls-area" className="video-controls">
                                <VideoPlayerTimeline onTimelineMouseDown={store.handleTimelineMouseDown} />
                                <Box sx={{ pointerEvents: 'all' }} display="flex" gap="20px" justifyContent="space-between" alignItems="flex-end">
                                    <Box display="flex" alignItems="center" gap="10px" flexGrow="1">
                                        <button onClick={store.togglePlay} className="control-btn">
                                            {!store.isPaused ? Icons.default.PauseIcon : Icons.default.PlayArrowIcon}
                                        </button>
                                        {finalControls.seekMinus10 && <button disabled={finalControls.seekMinus10 === "disabled"} onClick={store.seekMinusTen} className="control-btn">
                                            {Icons.default.Replay10Icon}
                                        </button>}
                                        {finalControls.seekPlus10 && <button disabled={finalControls.seekPlus10 === "disabled"} onClick={store.seekPlusTen} className="control-btn">
                                            {Icons.default.Forward10Icon}
                                        </button>}
                                        <VideoProgress />
                                    </Box>
                                    <Box display="flex" alignItems="center" gap="10px">
                                        <VolumeControl />
                                        <button onClick={store.toggleFullScreen} className="control-btn">
                                            {store.isInFullscreenMode ? Icons.default.FullscreenExitIcon : Icons.default.FullscreenIcon}
                                        </button>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>}
                    </Box>
                )}
            </Box>
        </ThemeProvider>
    )
} )

const VideoPlayer = memo( ( {
    controls = true,
    editOptions = EMPTY_OBJECT,
    src,
    containerHeight,
    title,
    thumb,
    defaults = EMPTY_OBJECT,
    controllerOptions,
    events = EMPTY_ARRAY,
    editMode = false,
    onEventsChange,
    onEvent,
    containerStyles = EMPTY_OBJECT,
    onReady,
    fullscreenElement,
    ...rest
} ) => {
    const storeRef = useRef( null )
    if ( !storeRef.current ) {
        storeRef.current = new VideoPlayerStore( {
            config: { defaults, controllerOptions, editOptions, events },
            controls: controls,
            onEvent,
            onEventsChange
        } )
    }

    const store = storeRef.current

    useEffect( () => {
        store.setConfig( { defaults, controllerOptions, editOptions } )
        store.setControls( controls )
        store.setCallbacks( { onEvent, onEventsChange } )
        store.setIsEditModeActive( editMode )
        store.setFullscreenElement( fullscreenElement )
    }, [store, defaults, controllerOptions, editOptions, onEvent, onEventsChange, controls, editMode, fullscreenElement] )

    useEffect( () => {
        store.setConfig( { events } )
    }, [events, store] )

    return (
        <VideoPlayerStoreProvider store={store}>
            <VideoPlayerInner
                editOptions={editOptions}
                src={src}
                containerHeight={containerHeight}
                title={title}
                thumb={thumb}
                defaults={defaults}
                events={events}
                editMode={editMode}
                onEventsChange={onEventsChange}
                onEvent={onEvent}
                containerStyles={containerStyles}
                onReady={onReady}
                {...rest}
            />
        </VideoPlayerStoreProvider>
    )
}
)
export default VideoPlayer